import { useRef } from 'react'
import type { OfferPageMultiRoomAutoselectionReason, OfferPageMultiRoomMode, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Sheet from 'components/utils/Sheet'
import OfferPageRoomModeToggleSection from 'components/OfferPage/MultipleRoomsBooking/Mobile/OfferPageRoomModeToggleSection'
import OfferPageTogglesSection from 'components/OfferPage/MultipleRoomsBooking/Mobile/OfferPageTogglesSection'
import { getCanUseMultiRoomUI } from 'selectors/offerPage/offerPageSelectors'
import ReturnCharterFlightTile from 'components/Flights/CharterBundle/ReturnCharterFlightTile'
import { toBaseFareClass, type FlightsClassTypes } from 'constants/flight'
import OfferPagePackagesHeading from 'components/OfferPage/MultipleRoomsBooking/Mobile/OfferPagePackagesHeading'
import Pane from 'components/Common/Pane'
import styled from 'styled-components'
import { rem } from 'polished'
import GrandHyattBaliFutureDatesBanner, {
  shouldShowGrandHyattBaliFutureDatesBanner,
} from 'components/OfferPage/FlashHotel/GrandHyattBaliFutureDatesBanner'
import { isMultipleRoomBookingActive } from 'selectors/offerPage/offerPageSharedSelectors'
import LayoutContainer from 'components/Common/LayoutContainer'
import MaldivesOfferOnePointPromoBanner from 'components/OfferPage/CharterBundle/MaldivesOfferOnePointPromoBanner'
import Caption from 'LuxKit/Typography/Caption'
import config from 'constants/config'
import { useIntl } from 'react-intl'
import OfferPageLoyaltyPointsSlider from 'components/OfferPage/Loyalty/OfferPageLoyaltyPointsSlider'
import useFlashRoomModeChange from '../packageSelection/useFlashRoomModeChange'
import MaldivesBundleCrossSellBanner from 'components/OfferPage/CharterBundle/MaldivesBundleCrossSellBanner'
import type { DateRange } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/types'

import CharterDurationToggle from './CharterDurationToggle'
import FlashHotelMobileIndividualRooms from './FlashHotelMobileIndividualRooms'
import FlashHotelMobileSameRoomOptions from './FlashHotelMobileSameRoomOptions'
import FlashPackageSelectionSection from '../packageSelection/FlashPackageSelectionSection'

const CharterDurationPane = styled(Pane)`
  padding: ${rem(20)};
  background-color: ${({ theme }) => theme.palette.neutral.surface.darkBackground};
`
interface Props {
  checkInDate?: string
  checkOutDate?: string
  offer: App.HotelOffer
  onViewPackageOptions?: () => void
  rooms: Array<OfferPageRoom>
  canUseMultiRoomUI: boolean
  roomMode: OfferPageMultiRoomMode
  autoselectionReason?: OfferPageMultiRoomAutoselectionReason
  flightOrigin?: App.Airport
  showFlightInformation: boolean
  charterBundleRoute?: App.CharterBundleRoute
  fareClass: FlightsClassTypes
  multipleRoomBookingActive: boolean
  optionD?: boolean
  charterFlightPreview?: DateRange
}

function FlashHotelMobileRoomOptions(props: Props) {
  const onFlashModeChange = useFlashRoomModeChange()
  const {
    checkInDate,
    checkOutDate,
    offer,
    rooms,
    canUseMultiRoomUI,
    roomMode,
    flightOrigin,
    showFlightInformation,
    autoselectionReason,
    charterBundleRoute,
    fareClass,
    multipleRoomBookingActive,
    optionD = false,
    charterFlightPreview,
  } = props

  const intl = useIntl()
  const hasCharterBundle = !!charterBundleRoute

  const headerRef = useRef<HTMLDivElement | null>(null)

  const showGrandHyattBaliFutureDatesBanner = shouldShowGrandHyattBaliFutureDatesBanner(offer.id)

  return <Sheet
    background="light-background"
    padding={hasCharterBundle ? '0 0 8 0' : '8 0'}
  >
    <VerticalSpacer gap={8}>
      {hasCharterBundle && <>
        <CharterDurationPane>
          <CharterDurationToggle />
        </CharterDurationPane>
        {charterBundleRoute && <ReturnCharterFlightTile
          route={charterBundleRoute}
          occupancies={rooms}
          checkInDate={checkInDate || charterFlightPreview?.checkIn}
          checkOutDate={checkOutDate || charterFlightPreview?.checkOut}
          fareClass={fareClass}
          flightOrigin={flightOrigin}
        />}
      </>}

      {canUseMultiRoomUI && <OfferPageRoomModeToggleSection
        onModeChange={optionD ? onFlashModeChange : undefined}
        roomMode={roomMode}
        rooms={rooms}
        saleUnit={offer.saleUnit}
        autoselectionReason={autoselectionReason}
        charterBundleRoute={charterBundleRoute}
        showGrandHyattBaliFutureDatesBanner={showGrandHyattBaliFutureDatesBanner}
      />}
      {!canUseMultiRoomUI && <VerticalSpacer gap={0}>
        <OfferPagePackagesHeading
          charterBundleRoute={charterBundleRoute}
        />
        {showGrandHyattBaliFutureDatesBanner && <GrandHyattBaliFutureDatesBanner />}
      </VerticalSpacer>}

      <MaldivesOfferOnePointPromoBanner inset />

      <OfferPageTogglesSection bundledWithFlightsOnly={offer.bundledWithFlightsOnly || !!charterBundleRoute} />

      <VerticalSpacer gap={0}>
        <OfferPageLoyaltyPointsSlider selectionMaximumActive={optionD && !multipleRoomBookingActive} />
        {!charterBundleRoute && <LayoutContainer><MaldivesBundleCrossSellBanner offer={offer} /></LayoutContainer>}
        {optionD && !multipleRoomBookingActive && <FlashPackageSelectionSection />}
        {multipleRoomBookingActive && <FlashHotelMobileIndividualRooms />}
        {!optionD && !multipleRoomBookingActive && <FlashHotelMobileSameRoomOptions showSelection={false} headerRef={headerRef} />}
      </VerticalSpacer>

      {showFlightInformation && (
        <LayoutContainer>
          <Caption variant="medium" align="center">
            {intl.formatMessage(
              {
                id: '1egv_hRhbjZJLsycMyqD',
                defaultMessage: '*Price is indicative and based on sample return fares (inclusive of booking fees on Low Cost Carriers). To view live pricing, conduct a full search. {brandName} full Terms and Conditions apply.',
                description: 'Disclaimer for flight information in the flight search or booking flow',
              },
              {
                brandName: config.brandConfig.name,
              },
            )}
          </Caption>
        </LayoutContainer>
      )}
    </VerticalSpacer>
  </Sheet>
}

type MappedProps = Pick<Props,
  | 'checkInDate'
  | 'checkOutDate'
  | 'rooms'
  | 'canUseMultiRoomUI'
  | 'roomMode'
  | 'autoselectionReason'
  | 'flightOrigin'
  | 'charterBundleRoute'
  | 'multipleRoomBookingActive'
  | 'fareClass'
> & { open: boolean }

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  checkInDate: state.checkInDate,
  checkOutDate: state.checkOutDate,
  open: !!state.activeFilter,
  rooms: state.rooms,
  canUseMultiRoomUI: getCanUseMultiRoomUI(state, appState),
  roomMode: state.multiRoomMode,
  autoselectionReason: state.multiRoomAutoselectionReason,
  flightOrigin: state.airport,
  charterBundleRoute: state.charterBundleRoute,
  multipleRoomBookingActive: isMultipleRoomBookingActive(state, appState),
  fareClass: toBaseFareClass(state.fareClass),
}), FlashHotelMobileRoomOptions)
